import { useState, useEffect } from "react";
import { Space, Skeleton, Col, Input, message } from "antd";
import Button from '@mui/material/Button';
import BankAccountBalanceModal from "../modals/balance";
import CurrencySingleValue from "../currency/singleValue";
import AccountHeaderFormatter from "./accountHeaderFormatter";
import { toPrice } from "../../../helpers/stringFormat";
import { backendPostWithoutRefresh } from "../../../helpers/callBackend";
import LoadingButton from '@mui/lab/LoadingButton';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import GenericTagChips from "../../specific/customTags/genericTagChips";

const BalanceInfo = (props) => {
	const [isEditing, setIsEditing] = useState(false);
	const [newVal, setNewVal] = useState("");
	const [value, setValue] = useState(toPrice(props.availableBalance));
	const [error, setError] = useState("");
	const [refreshBalanceLoading, setRefreshBalanceLoading] = useState(false);
	const [genericTags, setGenericTags] = useState([]);

	useEffect(() => {
		setValue(toPrice(props.availableBalance))
	}, [props.availableBalance])

	useEffect(() => {
		let formattedTags = []
		if (props?.purpose && props?.purpose?.name) {
			formattedTags.push({
				title: "Purpose",
				body: props.purpose.name
			})
		}

		const formattedGenericTags = (props.genericTags || []).map((entry) => {
			let title = ''
			let currPart = entry
			let parts = []
			while (currPart) {
				parts.push(currPart.name)
				if (!currPart.parent) {
					title = currPart.group.name
				}
				currPart = currPart.parent
			}
			return {
				title: title,
				body: parts.reverse().join(" / ")
			}
		})

		setGenericTags([...formattedTags, ...formattedGenericTags])

	}, [props.genericTags, props.purpose])

	const updateBankBalance = (response) => {
		props.setBankAccountData((prevState) => {
			return {
				...prevState,
				account: {
					...prevState.account,
					current_balance: response.current_balance,
					available_balance: response.available_balance,
					aggregated_balance: response.aggregated_balance,
					current_balance_raw: response.current_balance_raw,
					available_balance_raw: response.available_balance_raw,
					aggregated_balance_raw: response.aggregated_balance_raw
				}
			}
		})
	};

	const validate = (input) => {
		if (!/^\d+(\.\d{1,2})?$/.test(input)) {
			setError(`Balance must be a valid amount.`);
			message.error(error)
			return false;
		}
		setError('');
		return true;
	}

	const handleInputChange = (e) => {
		const input = e.target.value;
		setNewVal(input);
		validate(input);
	}

	const validateAndSave = async (input) => {
		if (!/^\d+(\.\d{1,2})?$/.test(input)) {
			setError(`Balance must be a valid amount.`);
			return false;
		}
		try {
			setIsEditing(true);
			setRefreshBalanceLoading(true);
			const raw = Number(input)
			const { data } = await backendPostWithoutRefresh("/api/bank-account/update/", { bank_account_id: props.bankAccountId, last_available_balance: raw, last_current_balance: raw });
			if (data) {
				updateBankBalance(data)
				backendPostWithoutRefresh("/api/bank-account/update-cache/", {
					accountId: props.bankAccountId,
					forceDynamicRefresh: true
				}, false, false)
					.then((res) => {
						props.setBankAccountData(res.data?.updated_account);
						setRefreshBalanceLoading(false);
					})
				setValue(input);
				setIsEditing(false);
				setNewVal("");
			}
		} catch (err) {
			setError("Save not successful. Try again.");
			message.error(error);
		}
	};

	const handleSave = () => {
		validateAndSave(newVal);
	};

	return (
		<AccountHeaderFormatter
			body={<Stack
				direction="column"
				spacing={3}
				sx={{
					justifyContent: "space-between",
					alignItems: "flex-start",
				}}
			>
				<Stack
					direction="row"
					flexWrap="wrap"
					alignItems="center"
					spacing={1} // Ensures consistent spacing between items
				>
					<Typography
						variant="h5"
						color="primary"
						sx={{ whiteSpace: { xs: "normal", sm: "nowrap" } }} // Prevents breaking on larger screens
					>
						{props.bankInfo?.bank_account_name || localStorage.getItem("account_name")}
					</Typography>

					<Typography fontStyle="italic" color="text.disabled">
						- {props.bankInfo?.bank?.name}
					</Typography>

					<Typography fontStyle="italic" color="text.disabled">
						- {props.bankInfo?.account_description}
					</Typography>
				</Stack>

				<Stack direction="column">
					<Stack direction="row"
						sx={{
							justifyContent: "flex-start",
							alignItems: "flex-end",
						}}>
						{(props.availableBalance !== undefined) ? (
							<div style={{ display: "flex", alignItems: "center" }}>
								{isEditing ? (
									<Col span={24} style={{ textAlign: "right", marginTop: 0, marginBottom: "1em", paddingTop: "4px", width: "100%" }}>
										<Space style={{ width: "100%" }} direction="horizontal">
											<Input
												value={newVal}
												placeholder={value}
												onChange={handleInputChange}
												style={{ flex: 1, minWidth: '82px' }}
											/>
											<Col span={24} style={{ width: "100%", display: 'flex', justifyContent: 'right', gap: '10px' }}>
												<LoadingButton
													sx={{ fontSize: 12, textTransform: 'none' }}
													disableElevation
													size="small"
													type="submit"
													variant="contained"
													color="primary"
													onClick={handleSave}
													disabled={!newVal || error}
													loading={refreshBalanceLoading}
												>
													Save
												</LoadingButton>
												<Button
													sx={{ fontSize: 12, textTransform: 'none' }}
													disableElevation
													size="small"
													type="submit"
													variant="outlined"
													color="secondary"
													onClick={() => setIsEditing(false)}
												>
													Cancel
												</Button>
											</Col>
										</Space>
									</Col>
								) : (
									<>
										<Col>

											<Typography variant={"h1"} marginRight={1}>
												<CurrencySingleValue
													currency={props.currency}
													currencySymbol={props.currencySymbol}
													amount={props.ICSBalance ? props.aggregatedBalance : props.availableBalance}
												/>
											</Typography>
										</Col>
										{props.allowBalanceEdit && (
											<Col span={3} style={{ textAlign: "right", marginLeft: "10px" }}>
												<Button
													sx={{ fontSize: 12, textTransform: 'none' }}
													disableElevation
													size="small"
													type="submit"
													variant="outlined"
													color="secondary"
													onClick={() => setIsEditing(true)}
												>
													Edit
												</Button>
											</Col>
										)}
									</>
								)}
							</div>
						)
							:
							<Skeleton paragraph={{ rows: 0, width: 100 }} active />
						}
						<BankAccountBalanceModal {...props} />
					</Stack>
					{props.lastBalanceRefreshComponent}
				</Stack>

				<GenericTagChips
					genericTags={props.genericTags}
					purpose={props.purpose}
				/>

			</Stack>}
		/>
	);
};

export default BalanceInfo;
